import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { auth, db } from '../../firebase/firebaseConfig';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { History, RotateCcw } from 'lucide-react';
import { COMMENT_KIND } from './useAccountComments';
import { formatDateMs } from './billingRecord';

// Inline-editable account comment for the Billing view.
//
// Free text an admin keeps against an account — "agreed start of billing",
// "waiting for response", "needs emailing". Deliberately spreadsheet-like:
// click the cell, type, Enter saves, Escape cancels, clicking away commits.
// No buttons, no author line, no metadata — it is a note column, not a record.
//
// Layout stability is a feature here, not polish: the display and edit states
// occupy exactly the same box (CELL_WIDTH wide, h-6 tall, one line), so
// entering and leaving edit mode cannot reflow the column or the table.
//
// Every save still appends a new revision to the append-only admin notes
// collection, so nothing is ever lost — the history control lists earlier
// versions with who wrote them and when. See useAccountComments.js for why
// storage works this way.

const MAX_LENGTH = 500;
// Fixed, and identical in both states — this is what stops the column moving.
const CELL_WIDTH = 'w-[220px]';
const HISTORY_WIDTH = 288; // px — matches the popover's fixed width
const HISTORY_MAX_HEIGHT = 256;

export default function AccountCommentCell({ accountUUID, comment, onSaved, onRetry, disabled, loading, failed }) {
  const [retrying, setRetrying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPos, setHistoryPos] = useState(null);
  const inputRef = useRef(null);
  // Leaving edit mode blurs the input; that blur must not re-trigger the very
  // save or cancel that caused it.
  const skipBlurRef = useRef(false);
  const historyRef = useRef(null);   // the cell wrapper
  const historyBtnRef = useRef(null);
  const popoverRef = useRef(null);   // portalled content, outside the wrapper

  const text = comment?.text || '';
  const history = comment?.history || [];

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  // The history popover is rendered in a portal on document.body rather than
  // inside the cell. The accounts table sits in an `overflow-x-auto` wrapper,
  // and a box that scrolls horizontally also clips vertically, so an in-flow
  // popover on one of the lower rows would be cut off. Fixed positioning from
  // the trigger's rect escapes that entirely.
  const toggleHistory = () => {
    if (showHistory) { setShowHistory(false); return; }
    const r = historyBtnRef.current?.getBoundingClientRect();
    if (r) {
      const left = Math.max(8, Math.min(r.right - HISTORY_WIDTH, window.innerWidth - HISTORY_WIDTH - 8));
      // Flip upwards when there isn't room below.
      const openUp = r.bottom + HISTORY_MAX_HEIGHT + 8 > window.innerHeight;
      setHistoryPos(openUp
        ? { left, bottom: window.innerHeight - r.top + 4 }
        : { left, top: r.bottom + 4 });
    }
    setShowHistory(true);
  };

  // Close the history popover on outside click / Escape while it's open. The
  // popover lives outside this component's DOM subtree, so "outside" has to
  // mean outside BOTH the cell and the portalled panel.
  useEffect(() => {
    if (!showHistory) return undefined;
    const onDown = (e) => {
      const inCell = historyRef.current && historyRef.current.contains(e.target);
      const inPopover = popoverRef.current && popoverRef.current.contains(e.target);
      if (!inCell && !inPopover) setShowHistory(false);
    };
    const onKeyCapture = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        setShowHistory(false);
      }
    };
    // A fixed-position panel would drift away from its anchor once anything
    // scrolls, so close instead of trying to track it.
    const onScrollOrResize = () => setShowHistory(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKeyCapture, true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKeyCapture, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [showHistory]);

  const startEditing = () => {
    if (disabled) return;
    setDraft(text);
    setSaveError(null);
    setEditing(true);
  };

  const cancel = () => {
    skipBlurRef.current = true; // leaving edit mode blurs the input; ignore it
    setEditing(false);
    setDraft('');
    setSaveError(null);
  };

  const save = async () => {
    const next = draft.trim();
    if (saving) return;
    // Unchanged — a cancel, not a pointless revision in the history.
    if (next === text.trim()) { cancel(); return; }
    // Emptying the field CLEARS the comment by appending an empty revision:
    // the storage is append-only, so this is how a stale comment is removed
    // while its wording survives in the history. Only meaningless when there
    // was nothing there to begin with.
    if (!next && !text) { cancel(); return; }

    setSaving(true);
    setSaveError(null);
    try {
      await addDoc(collection(db, 'accountNotes', accountUUID, 'notes'), {
        text: next.slice(0, MAX_LENGTH),
        kind: COMMENT_KIND,
        adminEmail: auth.currentUser?.email || 'unknown',
        createdAt: serverTimestamp(),
      });
      skipBlurRef.current = true;
      setEditing(false);
      setDraft('');
      if (onSaved) await onSaved(accountUUID);
    } catch (e) {
      console.error('Failed to save account comment:', e);
      setSaveError('NOT saved' +
        (e.code === 'permission-denied' ? ' — permission denied' : ` — ${e.code || e.message}`) +
        '. Your text is kept; try again.');
    }
    setSaving(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    }
  };

  // Spreadsheet behaviour: clicking away commits what was typed, exactly as
  // Enter would. Nothing is discarded silently and nothing is left half-open.
  const onBlur = () => {
    if (skipBlurRef.current) { skipBlurRef.current = false; return; }
    if (draft.trim() === text.trim()) cancel();
    else save();
  };

  if (editing) {
    return (
      <div className={CELL_WIDTH}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          maxLength={MAX_LENGTH}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          placeholder="Comment…"
          // Same box as the display state — identical width and height — so
          // entering edit mode cannot reflow the column or the row.
          className="w-full h-6 rounded-sm border border-input bg-background px-1 text-xs outline-none focus:ring-1 focus:ring-ring"
        />
        {saveError && <div className="text-[10px] text-red-600 mt-0.5">{saveError}</div>}
      </div>
    );
  }

  // Until this account's comment has actually been read, an empty cell must not
  // claim there is no comment — an admin could type over one that is still
  // arriving and push the existing wording out of view. The same applies when
  // the read failed: keep whatever was last read, say the state is unknown, and
  // don't invite an edit. A blank, editable cell always means "no comment".
  const pending = !!loading && !comment;
  const unreadable = !!failed && !pending;
  const locked = disabled || pending || unreadable;

  return (
    <div className={`relative ${CELL_WIDTH}`} ref={historyRef}>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={startEditing}
          disabled={locked}
          // The comment itself is the tooltip: the cell is one line, so a note
          // longer than the column is read by hovering rather than by wrapping
          // (which would change the row height between display and edit).
          title={pending
            ? 'Loading…'
            : unreadable
              ? 'This comment could not be read, so it cannot be edited right now'
              : disabled
                ? 'Comments are unavailable right now'
                : text || 'Click to add a comment'}
          className="flex-1 min-w-0 h-6 flex items-center text-left text-xs rounded-sm px-1 hover:bg-muted disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          {pending ? (
            <span className="text-muted-foreground">…</span>
          ) : unreadable && !text ? (
            <span className="text-amber-700 italic">unavailable</span>
          ) : text ? (
            <span className="truncate">{text}</span>
          ) : (
            <span className="text-muted-foreground italic">Add comment…</span>
          )}
        </button>
        {/* A failed read locks the cell, so a save can never be the thing that
            recovers it. Without this the only way back is toggling a filter or
            leaving and re-entering the view, which nothing tells the admin. */}
        {unreadable && onRetry && (
          <button
            type="button"
            disabled={retrying}
            onClick={async () => {
              setRetrying(true);
              try { await onRetry(accountUUID); } finally { setRetrying(false); }
            }}
            title="Try reading this comment again"
            className="p-1 rounded-sm text-amber-700 hover:bg-amber-50 flex-shrink-0 disabled:opacity-50"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${retrying ? 'animate-spin' : ''}`} />
          </button>
        )}
        {history.length > 0 && (
          <button
            type="button"
            ref={historyBtnRef}
            onClick={toggleHistory}
            title={`${history.length} earlier ${history.length === 1 ? 'version' : 'versions'}`}
            aria-expanded={showHistory}
            className="p-1 rounded-sm text-muted-foreground hover:bg-muted flex-shrink-0"
          >
            <History className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {showHistory && history.length > 0 && historyPos && createPortal(
        <div
          ref={popoverRef}
          style={{ position: 'fixed', width: HISTORY_WIDTH, maxHeight: HISTORY_MAX_HEIGHT, ...historyPos }}
          className="z-50 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md p-2"
        >
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Earlier versions
          </div>
          <div className="space-y-1.5">
            {history.map((h) => (
              <div key={h.id} className="text-xs border-l-2 border-muted pl-2">
                <div className="whitespace-pre-wrap break-words">{h.text || <span className="italic text-muted-foreground">(cleared)</span>}</div>
                <div className="text-[10px] text-muted-foreground">
                  {h.adminEmail || '?'} · {formatDateMs(h.createdAtMs)}
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
